import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, Loader2, Package, User, Calendar, 
  FileText, AlertCircle, CheckCircle, XCircle, 
  RefreshCw, DollarSign, Send, Truck, ScrollText
} from 'lucide-react';
import axios from 'axios';
import RecordAuditTrail from '../components/audit/RecordAuditTrail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BillingView = () => {
  const navigate = useNavigate();
  const { billingNumber } = useParams();
  const [billing, setBilling] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReleasing, setIsReleasing] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [viewTab, setViewTab] = useState('overview');


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [billingRes, custRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/otc/billing-documents/${billingNumber}`),
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }))
      ]);
      setBilling(billingRes.data);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading billing document:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load billing document: ${errorMsg}`);
      navigate('/otc/billing');
    } finally {
      setIsLoading(false);
    }
  }, [billingNumber, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CREATED': return 'bg-orange-100 text-orange-800';
      case 'RELEASED': return 'bg-yellow-100 text-yellow-800';
      case 'POSTED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'POSTED': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'CANCELLED': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'RELEASED': return <Send className="w-5 h-5 text-yellow-600" />;
      default: return <DollarSign className="w-5 h-5 text-orange-600" />;
    }
  };

  const handleRelease = async () => {
    if (!window.confirm('Are you sure you want to release this billing document for accounting?')) {
      return;
    }

    setIsReleasing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/otc/billing-documents/${billingNumber}/release`);
      toast.success('Billing document released for accounting!');
      loadData();
    } catch (error) {
      console.error('Error releasing billing document:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to release billing document: ${errorMsg}`);
    } finally {
      setIsReleasing(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading billing document...</span>
        </div>
      </Layout>
    );
  }

  if (!billing) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-gray-600">Billing document not found</p>
          <Button onClick={() => navigate('/otc/billing')} className="mt-4">
            Back to Billing
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/billing')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Invoice {billing.billing_number}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(billing.status)}`}>
                  {getStatusIcon(billing.status)}
                  {billing.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Payer: {getCustomerName(billing.payer)} | Date: {billing.billing_date?.split('T')[0]}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {billing.status === 'CREATED' && (
              <Button onClick={handleRelease} disabled={isReleasing} data-testid="release-billing-btn">
                {isReleasing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Releasing...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Release for Accounting
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-1 border-b border-gray-200 mb-6">
          <button onClick={() => setViewTab('overview')} data-testid="billing-tab-overview"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${viewTab === 'overview' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            Overview
          </button>
          <button onClick={() => setViewTab('audit')} data-testid="billing-tab-audit"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${viewTab === 'audit' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            <ScrollText className="w-4 h-4" /> Audit Trail
          </button>
        </div>

        {viewTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Data */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Invoice Data
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Billing Type</p>
                  <p className="font-medium">{billing.billing_type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sales Order</p>
                  {billing.sales_order_number ? (
                    <p className="font-mono font-medium text-orange-600 cursor-pointer hover:underline"
                       onClick={() => navigate(`/otc/sales-orders/${billing.sales_order_number}`)}>
                      {billing.sales_order_number}
                    </p>
                  ) : (
                    <p className="text-gray-400">-</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">Delivery</p>
                  {billing.delivery_number ? (
                    <p className="font-mono font-medium text-orange-600 cursor-pointer hover:underline"
                       onClick={() => navigate(`/otc/deliveries/${billing.delivery_number}`)}>
                      {billing.delivery_number}
                    </p>
                  ) : (
                    <p className="text-gray-400">-</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sales Organization</p>
                  <p className="font-medium">{billing.sales_organization}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Distribution Channel</p>
                  <p className="font-medium">{billing.distribution_channel || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Currency</p>
                  <p className="font-medium">{billing.currency}</p>
                </div>
              </div>
            </Card>

            {/* Partner Data */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Partners
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Payer</p>
                  <p className="font-medium">{billing.payer}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(billing.payer)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bill-to Party</p>
                  <p className="font-medium">{billing.bill_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(billing.bill_to_party)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sold-to Party</p>
                  <p className="font-medium">{billing.sold_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(billing.sold_to_party)}</p>
                </div>
              </div>
            </Card>

            {/* Items */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Invoice Items ({billing.items?.length || 0})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="billing-items-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-3 text-sm font-semibold">Item</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Material</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Description</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Quantity</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Net Price</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Net Value</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Tax</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Gross Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(billing.items || []).map((item, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono">{item.item_number?.toString().padStart(4, '0')}</td>
                        <td className="py-2 px-3">{item.material_id || '-'}</td>
                        <td className="py-2 px-3">{item.item_description}</td>
                        <td className="py-2 px-3 text-right">
                          {item.billing_quantity} {item.unit_of_measure}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {item.currency} {item.net_price?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {item.currency} {item.net_value?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-red-600">
                          {item.currency} {item.tax_amount?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono font-bold">
                          {item.currency} {item.gross_value?.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Totals */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Invoice Totals
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Net Value:</span>
                  <span className="font-mono">{billing.currency} {billing.net_value?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax Amount:</span>
                  <span className="font-mono text-red-600">+ {billing.currency} {billing.tax_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-200">
                  <span className="font-semibold">Gross Value:</span>
                  <span className="font-mono font-bold text-lg">{billing.currency} {billing.gross_value?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
            </Card>

            {/* Dates & Payment */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Dates & Payment
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Billing Date</p>
                  <p className="font-medium">{billing.billing_date?.split('T')[0]}</p>
                </div>
                {billing.pricing_date && (
                  <div>
                    <p className="text-xs text-gray-500">Pricing Date</p>
                    <p className="font-medium">{billing.pricing_date?.split('T')[0]}</p>
                  </div>
                )}
                {billing.payment_terms && (
                  <div>
                    <p className="text-xs text-gray-500">Payment Terms</p>
                    <p className="font-medium">{billing.payment_terms}</p>
                  </div>
                )}
                {billing.due_date && (
                  <div>
                    <p className="text-xs text-gray-500">Due Date</p>
                    <p className="font-medium">{billing.due_date?.split('T')[0]}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Document Flow */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Document Flow</h3>
              <div className="space-y-3">
                {billing.sales_order_number && (
                  <div className="flex items-center gap-2 p-2 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100"
                       onClick={() => navigate(`/otc/sales-orders/${billing.sales_order_number}`)}>
                    <FileText className="w-4 h-4 text-orange-600" />
                    <div>
                      <p className="text-sm font-medium text-orange-800">Sales Order</p>
                      <p className="text-xs text-orange-600">{billing.sales_order_number}</p>
                    </div>
                  </div>
                )}
                {billing.delivery_number && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 ml-4"
                       onClick={() => navigate(`/otc/deliveries/${billing.delivery_number}`)}>
                    <Truck className="w-4 h-4 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Delivery</p>
                      <p className="text-xs text-green-600">{billing.delivery_number}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2 p-2 bg-orange-100 rounded-lg ml-8">
                  <DollarSign className="w-4 h-4 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-orange-800">Invoice</p>
                    <p className="text-xs text-orange-600">{billing.billing_number}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Notes */}
            {billing.header_note && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{billing.header_note}</p>
              </Card>
            )}
          </div>
        </div>
        )}
        {viewTab === 'audit' && (
          <RecordAuditTrail doctype="billing_document" recordId={billingNumber} />
        )}
      </div>
    </Layout>
  );
};

export default BillingView;
